import { MatrizInline } from '@/components/MatrizInline';
import { TernaRevisionStepper } from '@/components/revision/TernaRevisionStepper';
import { CorreccionesChat } from '@/components/asesorias/CorreccionesChat';
import { useAprobarAsesoria, useAsesoriaHistorialDetalle, useEvaluacionAdmin, useLatestHistorialMap, useRechazarAsesoria } from '@/hooks';
import { dashboardService } from '@/services';
import { getAsesoriaEstadoLabel, normalizeAsesoriaEstado, type AsesoriaEstado } from '@/utils/asesoriaStatus';
import { buildHistorialFieldMap } from '@/hooks/useLatestHistorialMap';
import { agruparHistorialPorElemento, historialKey, type RevisionEntry } from '@/utils/revisionHistorial';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { OficialPrefix } from '@/components/ui/OficialHint';
import { Tooltip } from '@/components/ui/Tooltip';
import { EvidenciasTab, AudiosTab, TrazabilidadTab } from '@/pages/admin/AdminRevisionDetailPage';
import type { AsesoriaPendiente } from '@/types/types';
import type { DiffActivaTernaCambios } from '@/types/evaluacionAdmin';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  FileAudio,
  FileText,
  LayoutGrid,
  MessageSquare,
  Target,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────

function formatDate(d?: string | null) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatHours(h: number | null | undefined) {
  if (h === null || h === undefined) return '—';
  const totalSeconds = Math.round(h * 3600);
  const hrs = Math.floor(totalSeconds / 3600);
  const min = Math.floor((totalSeconds % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function formatDateTime(iso: string) {
  try {
    const date = new Date(iso);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function estadoChip(estado: AsesoriaEstado | null, raw: string) {
  const label = estado ? getAsesoriaEstadoLabel(estado) : raw;

  const variants: Record<string, { bg: string; fg: string }> = {
    EN_CURSO: { bg: 'var(--admin-blue-bg)', fg: 'var(--admin-blue)' },
    FINALIZADA: { bg: 'var(--admin-green-bg)', fg: 'var(--admin-green)' },
    EN_REVISION: { bg: 'var(--admin-amber-bg)', fg: 'var(--admin-amber)' },
    RECHAZADA: { bg: 'var(--admin-red-bg)', fg: 'var(--admin-red)' },
  };
  const v = variants[estado ?? ''] ?? { bg: 'var(--admin-surface2)', fg: 'var(--admin-text2)' };

  return (
    <span className="admin-chip" style={{ background: v.bg, color: v.fg }}>
      {label}
    </span>
  );
}

function parseRecomendaciones(text?: string | null) {
  if (!text) return { original: null, rechazos: [] };
  const lines = text.split('\n');
  const rechazos: { fecha: string; motivo: string }[] = [];
  const originalLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(/^\[(.*?)\] RECHAZO ADMIN:(?:\s*(.*))?/);
    if (match) {
      let motivo = match[2] || '';
      // Si el motivo está en la siguiente línea (porque se guardó con salto de línea)
      if (!motivo && i + 1 < lines.length && !lines[i + 1].match(/^\[(.*?)\] RECHAZO ADMIN:/)) {
        motivo = lines[i + 1].trim();
        i++; // skip next line
      }
      rechazos.push({ fecha: match[1], motivo });
    } else {
      originalLines.push(line);
    }
  }

  const original = originalLines.join('\n').trim();
  return { original: original || null, rechazos };
}

// ─── Sub-components ─────────────────────────────────

function AprobarModal({
  empresaNombre,
  onClose,
  onConfirm,
  isLoading,
  result,
  error,
}: {
  empresaNombre: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  result: { message: string; actaUrl: string | null } | null;
  error: string | null;
}) {
  const newLocal = 'w-10 h-10 bg-(--admin-green-bg) rounded-full flex items-center justify-center';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-(--admin-surface) rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 font-['DM_Sans']"
        onClick={(e) => e.stopPropagation()}
      >
        {result ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-(--admin-green-bg) rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-(--admin-green)" />
              </div>
              <h3 className="text-[16px] font-semibold text-(--admin-text)">Asesoría Aprobada</h3>
            </div>
            <p className="text-(--admin-text2) text-[13px] mb-4">{result.message}</p>
            {result.actaUrl && (
              <a
                href={result.actaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-(--admin-blue) font-medium text-[13px] mb-4"
              >
                <Download className="w-4 h-4" /> Descargar Acta
              </a>
            )}
            <button type="button" className="admin-btn primary w-full justify-center" onClick={onClose}>
              Cerrar
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className={newLocal}>
                <CheckCircle className="w-5 h-5 text-(--admin-green)" />
              </div>
              <h3 className="text-[16px] font-semibold text-(--admin-text)">Aprobar Asesoría</h3>
            </div>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-[#fdf0ee] border border-[#f5c6c0] flex items-start gap-2 text-[#c0392b] text-[11px]">
                <AlertCircle className="w-4.5 h-4.5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <p className="text-(--admin-text2) text-[13px] mb-6">
              ¿Confirmas la aprobación de la asesoría de <strong>{empresaNombre}</strong>?
            </p>
            <div className="flex gap-3">
              <button type="button" className="admin-btn flex-1 justify-center" onClick={onClose} disabled={isLoading}>
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn bg-(--admin-green-bg) text-(--admin-green) border-transparent flex-1 justify-center"
                onClick={onConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Aprobando...' : 'Aprobar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DenegarModal({
  empresaNombre,
  onClose,
  onConfirm,
  isLoading,
}: {
  empresaNombre: string;
  onClose: () => void;
  onConfirm: (motivo?: string) => void;
  isLoading: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-(--admin-surface) rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 font-['DM_Sans']"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-(--admin-red-bg) rounded-full flex items-center justify-center">
            <XCircle className="w-5 h-5 text-(--admin-red)" />
          </div>
          <h3 className="text-[16px] font-semibold text-(--admin-text)">Denegar Asesoría</h3>
        </div>
        <p className="text-(--admin-text2) text-[13px] mb-4">
          La asesoría de <strong>{empresaNombre}</strong> será devuelta al asesor.
        </p>
        <div className="mb-6">
          <label htmlFor="motivo" className="block text-[12px] font-medium text-(--admin-text2) mb-1">
            Motivo (opcional)
          </label>
          <textarea
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivos o correcciones necesarias..."
            className="w-full px-3 py-2 border border-(--admin-border2) rounded-lg text-[13px] resize-none focus:outline-2 focus:outline-[#1a1a1a] focus:outline-offset-1 font-['DM_Sans']"
            rows={3}
          />
        </div>
        <div className="flex gap-3">
          <button type="button" className="admin-btn flex-1 justify-center" onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>
          <button
            type="button"
            className="admin-btn bg-(--admin-red-bg) text-(--admin-red) border-transparent flex-1 justify-center"
            onClick={() => onConfirm(motivo || undefined)}
            disabled={isLoading}
          >
            {isLoading ? 'Denegando...' : 'Denegar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AudioItem({ audio, historialEntries, asesorNombre }: { audio: any; historialEntries?: RevisionEntry[]; asesorNombre?: string }) {
  const googleId = audio.googleId || audio.adjunteDelAudio;
  const duracion = audio.duracion || audio.duracionSeg || 0;
  const hasChat = (historialEntries?.length ?? 0) > 0;

  const conArchivo = useMemo(() => {
    if (!historialEntries) return [];
    return historialEntries.map((e) => {
      if (e.estado !== 'SISTEMA_AUDIO_VINCULADO' || e.elementoId == null) return e;
      return {
        ...e,
        archivoAdjunto: {
          nombre: `Audio · ${asesorNombre || 'Asesor'}`,
          url: `https://drive.google.com/file/d/${googleId}/preview`,
          duracionSeg: duracion,
        },
      };
    });
  }, [historialEntries, asesorNombre, googleId, duracion]);

  return (
    <div style={{ borderBottom: '1px solid var(--admin-border)' }}>
      <a
        href={`https://drive.google.com/file/d/${googleId}/preview`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 py-3 px-3 rounded-lg transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--admin-purple-bg)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--admin-purple-bg)' }}>
          <FileAudio className="w-4 h-4" style={{ color: 'var(--admin-purple)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium truncate" style={{ color: 'var(--admin-text)' }}>
            Grabación de asesoría
          </div>
          <div className="text-[11px]" style={{ color: 'var(--admin-text3)' }}>
            {formatDate(audio.fechaGrabacion)}
          </div>
        </div>
        <span className="admin-chip text-[10px]" style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)' }}>
          {formatDuration(duracion)}
        </span>
      </a>
      {hasChat && (
        <div className="px-3 pb-3 pt-1">
          <CorreccionesChat entries={conArchivo} actorActual="admin" canReply={false} className="w-full" />
        </div>
      )}
    </div>
  );
}

function EvidenciaItem({
  evidencia,
  historialEntries,
}: {
  evidencia: {
    evidenciaId: number;
    nombre: string;
    descripcion: string | null;
    fechaAdicion: string | Date;
    url: string;
    tipoArchivo: string | null;
  };
  historialEntries?: RevisionEntry[];
}) {
  const hasChat = (historialEntries?.length ?? 0) > 0;

  const conArchivo = useMemo(() => {
    if (!historialEntries) return [];
    return historialEntries.map((e) => {
      if (e.estado !== 'SISTEMA_EVIDENCIA_SUBIDA' || e.elementoId == null) return e;
      return {
        ...e,
        archivoAdjunto: { nombre: evidencia.nombre, url: evidencia.url, descripcion: evidencia.descripcion ?? null },
      };
    });
  }, [historialEntries, evidencia]);

  return (
    <div style={{ borderBottom: '1px solid var(--admin-border)' }}>
      <a
        href={evidencia.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 py-3 px-3 rounded-lg transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--admin-green-bg)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--admin-green-bg)' }}>
          <FileText className="w-4 h-4" style={{ color: 'var(--admin-green)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium truncate" style={{ color: 'var(--admin-text)' }}>
            {evidencia.nombre}
          </div>
          {evidencia.descripcion && (
            <div className="text-[11px] line-clamp-1" style={{ color: 'var(--admin-text3)' }}>
              {evidencia.descripcion}
            </div>
          )}
          <div className="text-[10px]" style={{ color: 'var(--admin-text3)' }}>
            {formatDateTime(evidencia.fechaAdicion instanceof Date ? evidencia.fechaAdicion.toISOString() : evidencia.fechaAdicion)}
          </div>
        </div>
      </a>
      {hasChat && (
        <div className="px-3 pb-3 pt-1">
          <CorreccionesChat entries={conArchivo} actorActual="admin" canReply={false} className="w-full" />
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────

type RevTab = 'evaluacion' | 'evidencias' | 'audios' | 'trazabilidad' | 'historial';

export function AsesoriaHistorialDetallePage() {
  const { nit, asesoriaId } = useParams<{ nit: string; asesoriaId: string }>();
  const navigate = useNavigate();
  const numericAsesoriaId = asesoriaId ? Number(asesoriaId) : null;

  const {
    data: asesoria,
    isLoading: isLoadingAsesoria,
    error: errorAsesoria,
  } = useAsesoriaHistorialDetalle(nit ?? null, numericAsesoriaId);
  const { data: evaluacion, isLoading: isLoadingEvaluacion, error: errorEvaluacion } = useEvaluacionAdmin(numericAsesoriaId);

  const { aprobar, isLoading: aprobando, error: errorAprobar } = useAprobarAsesoria();
  const { rechazar, isLoading: rechazando } = useRechazarAsesoria();

  const [activeTab, setActiveTab] = useState<RevTab>('evaluacion');
  const [revisionView, setRevisionView] = useState<'matrix' | 'stepper'>('matrix');
  const [isExporting, setIsExporting] = useState(false);
  const [modalType, setModalType] = useState<'aprobar' | 'denegar' | null>(null);
  const [aprobarResult, setAprobarResult] = useState<{ message: string; actaUrl: string | null } | null>(null);
  const [historialRevision, setHistorialRevision] = useState<any[]>([]);

  useEffect(() => {
    if (nit && numericAsesoriaId) {
      dashboardService
        .getRevisionHistorial(nit, numericAsesoriaId)
        .then((res) => {
          if (res.success) setHistorialRevision(res.historial || []);
        })
        .catch((err) => console.error('Error al cargar historial de revisión:', err));
    }
  }, [nit, numericAsesoriaId]);

  const historialFieldMap = useMemo(() => buildHistorialFieldMap(historialRevision as any), [historialRevision]);
  const latestHistorialMap = useLatestHistorialMap(historialRevision as any);

  const diffTernaMap = useMemo(() => {
    const m = new Map<string, DiffActivaTernaCambios>();
    const diffTernas = evaluacion?.diffActiva?.ternas;
    if (!diffTernas) return m;
    if (Array.isArray(diffTernas)) {
      diffTernas.forEach((d: any) => {
        const cambios = d.cambios || d;
        if (d.ternaId) m.set(`terna:${d.ternaId}`, cambios);
        if (d.grupo) m.set(`grupo:${d.grupo}`, cambios);
      });
    } else {
      Object.entries(diffTernas).forEach(([id, val]: [string, any]) => {
        const key = !isNaN(Number(id)) ? `terna:${id}` : `grupo:${id}`;
        const cambios: DiffActivaTernaCambios = {};
        if (typeof val.observaciones === 'string') {
          cambios.observaciones = { activa: val.observaciones, anterior: null };
        } else if (val.observaciones) {
          cambios.observaciones = val.observaciones;
        }
        if (typeof val.planMejora === 'string') {
          cambios.planMejora = { activa: val.planMejora, anterior: null };
        } else if (val.planMejora) {
          cambios.planMejora = val.planMejora;
        }
        if (val.fechaImplementacion) cambios.fechaImplementacion = val.fechaImplementacion;
        if (val.evidenciaIds) cambios.evidenciaIds = val.evidenciaIds;
        m.set(key, cambios);
      });
    }
    return m;
  }, [evaluacion?.diffActiva]);

  const asesoriaAdaptada = useMemo(() => {
    if (!asesoria) return null;
    return {
      ...asesoria,
      empresa: {
        ...asesoria.empresa,
        nitId: asesoria.empresa?.nit || nit || '',
      },
      evidenciasGenerales: asesoria.evidencias || [],
      // Este endpoint (historial) devuelve { duracion, googleId }, no { duracionSeg, adjunteDelAudio }
      // como el resto de la app — sin este mapeo, AudiosTab mostraba siempre "0s".
      audios: (asesoria.audios || []).map((a) => ({
        ...a,
        duracionSeg: a.duracionSeg ?? a.duracion ?? 0,
        adjunteDelAudio: a.adjunteDelAudio ?? (a as unknown as { googleId?: string }).googleId ?? '',
        asesorNombre: asesoria.asesor?.nombre ?? 'Asesor',
      })),
    } as unknown as AsesoriaPendiente;
  }, [asesoria, nit]);

  const cadenasEvidencias = useMemo(() => {
    if (!asesoria?.evidencias) return [];
    return agruparCadenaArchivos(
      asesoria.evidencias.filter((e) => (e as any).estado !== 'ELIMINADO'),
      (e) => e.evidenciaId,
      (e) => (e as any).reemplazaEvidenciaId ?? null
    );
  }, [asesoria?.evidencias]);

  const cadenasAudios = useMemo(() => {
    if (!asesoria?.audios) return [];
    return agruparCadenaArchivos(
      asesoria.audios,
      (a) => a.audioId ?? 0,
      (a) => (a as any).reemplazaAudioId ?? null
    );
  }, [asesoria?.audios]);

  const tabs: { id: RevTab; label: string; count?: number }[] = useMemo(() => {
    return [
      { id: 'evaluacion', label: 'Evaluación NTC' },
      { id: 'evidencias', label: 'Evidencias', count: cadenasEvidencias.length },
      { id: 'audios', label: 'Audios', count: cadenasAudios.length },
      { id: 'trazabilidad', label: 'Trazabilidad' },
      { id: 'historial', label: 'Historial asesorías' },
    ];
  }, [cadenasEvidencias.length, cadenasAudios.length]);

  const closeModal = () => {
    setModalType(null);
    setAprobarResult(null);
  };

  const handleAprobar = async () => {
    if (!numericAsesoriaId) return;
    const res = await aprobar(numericAsesoriaId);
    if (res?.success) {
      setAprobarResult(res.data);
    }
  };

  const handleDenegar = async (motivo?: string) => {
    if (!numericAsesoriaId) return;
    const ok = await rechazar(numericAsesoriaId, motivo);
    if (ok) {
      closeModal();
      navigate(`/empresas/${nit}`);
    }
  };

  const handleExportExcel = async () => {
    if (!numericAsesoriaId) return;
    try {
      setIsExporting(true);
      const blob = await dashboardService.downloadAsesoriaExcelReport(numericAsesoriaId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `matriz_asesoria_${nit}_${asesoriaId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al exportar:', err);
      alert('Error al generar el archivo Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const evidenciasOrdenadas = useMemo(() => {
    if (!asesoria?.evidencias) return [];
    return [...asesoria.evidencias].sort((a, b) => new Date(b.fechaAdicion).getTime() - new Date(a.fechaAdicion).getTime());
  }, [asesoria?.evidencias]);

  const audiosOrdenados = useMemo(() => {
    if (!asesoria?.audios) return [];
    return [...(asesoria.audios || [])].sort((a, b) => new Date(b.fechaGrabacion).getTime() - new Date(a.fechaGrabacion).getTime());
  }, [asesoria?.audios]);

  const { original: recomendacionesLimpias, rechazos } = useMemo(
    () => parseRecomendaciones(asesoria?.recomendaciones),
    [asesoria?.recomendaciones]
  );

  const estadoAsesoria = asesoria ? normalizeAsesoriaEstado({ estado: asesoria.estado, autoAprobar: asesoria.autoAprobar }) : null;
  const cambio = (asesoria as any)?.resumenEvaluacion?.cambio ?? 0;

  const isLoading = isLoadingAsesoria || isLoadingEvaluacion;
  const error = errorAsesoria || errorEvaluacion;

  if (isLoading) {
    return (
      <div className="p-12 text-center" style={{ padding: '24px 28px' }}>
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-[12px]" style={{ color: 'var(--admin-text3)' }}>
          Cargando detalle de asesoría…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-detail-panel text-center py-12" style={{ margin: '24px 28px' }}>
        <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--admin-red)' }} />
        <p className="text-[15px] font-semibold mb-1" style={{ color: 'var(--admin-red)' }}>
          Error al cargar
        </p>
        <p className="text-[12px]" style={{ color: 'var(--admin-text3)' }}>
          {error}
        </p>
      </div>
    );
  }

  if (!asesoria) {
    return (
      <div className="admin-detail-panel text-center py-12" style={{ margin: '24px 28px' }}>
        <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--admin-amber)' }} />
        <p className="text-[15px] font-semibold" style={{ color: 'var(--admin-text)' }}>
          Asesoría no encontrada
        </p>
      </div>
    );
  }

  return (
    <div
      style={{ padding: '24px 28px', background: 'var(--admin-bg)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Back button */}
      <button
        type="button"
        className="flex items-center gap-1.5 text-[12px] mb-5 hover:opacity-70 transition-opacity"
        style={{ color: 'var(--admin-text2)' }}
        onClick={() => navigate(`/empresas/${nit}`)}
      >
        ← Volver a la empresa
      </button>

      {/* Header Panel */}
      <div className="admin-detail-panel">
        {estadoAsesoria === 'EN_REVISION' && (
          <div className="flex items-center gap-3 p-4 mb-5 rounded-lg bg-(--admin-amber-bg) border border-[#f0dba0]">
            <div className="flex-1">
              <span className="text-[13px] font-semibold text-(--admin-amber)">
                Esta asesoría está pendiente de revisión
                {cambio !== 0 && (
                  <span className={`ml-2 font-bold ${cambio > 0 ? 'text-(--admin-green)' : 'text-(--admin-red)'}`}>
                    ({cambio > 0 ? `+${cambio.toFixed(1)}%` : `${cambio.toFixed(1)}%`})
                  </span>
                )}
              </span>
              <p className="text-[11px] text-(--admin-text2) mt-0.5">
                Horas totales:{' '}
                {(asesoria as any)?.horasAntes != null && (asesoria as any)?.horasDespues != null ? (
                  <strong>
                    <span className="text-(--admin-text3)">
                      {formatHours((asesoria as any).horasAntes)} ({((asesoria as any).porcentajeHorasAntes ?? 0).toFixed(1)}%)
                    </span>
                    {' → '}
                    <span className="text-(--admin-green)">
                      {formatHours((asesoria as any).horasDespues)} ({((asesoria as any).porcentajeHorasDespues ?? 0).toFixed(1)}%)
                    </span>
                  </strong>
                ) : (
                  <strong>
                    {formatHours(asesoria.horas)} {asesoria.horas != null && `(+${((asesoria.horas / 320) * 100).toFixed(1)}%)`}
                  </strong>
                )}
                {'. '}Esta asesoría está siendo revisada en la sección de auditoría.
              </p>
            </div>
            <div>
              <button
                type="button"
                className="admin-btn bg-(--admin-amber-bg) text-(--admin-amber) hover:opacity-85 font-semibold flex items-center gap-1.5 cursor-pointer text-[12px] px-3.5 py-1.5"
                onClick={() => navigate(`/revision/${asesoria.asesoriaId}`)}
              >
                <span>Ir a la revisión</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--admin-text3)' }}>
              Empresa
            </div>
            <div className="text-[18px] font-semibold" style={{ color: 'var(--admin-text)' }}>
              {asesoria.empresa?.nombre || 'Empresa'}
            </div>
            <div className="text-[12px] font-mono" style={{ color: 'var(--admin-text3)' }}>
              NIT: {asesoria.empresa?.nit || nit}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {asesoria.actaLink && (
              <a
                href={asesoria.actaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue) hover:bg-(--admin-blue) hover:text-white"
              >
                <FileText className="w-3.5 h-3.5" />
                Ver acta
              </a>
            )}
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                isExporting
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue) hover:bg-(--admin-blue) hover:text-white'
              }`}
            >
              {isExporting ? (
                <>
                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5" />
                  Exportar a Excel
                </>
              )}
            </button>
            {estadoChip(estadoAsesoria, asesoria.estado)}
          </div>
        </div>

        <div className="admin-detail-grid">
          <div className="admin-detail-field">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-3.5 h-3.5" style={{ color: 'var(--admin-blue)' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--admin-text3)' }}>
                Objetivo
              </span>
            </div>
            <div className="text-[13px] font-medium" style={{ color: 'var(--admin-text)' }}>
              {asesoria.objetivo || '—'}
            </div>
          </div>

          <div className="admin-detail-field">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="w-3.5 h-3.5" style={{ color: 'var(--admin-text3)' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--admin-text3)' }}>
                Asesor
              </span>
            </div>
            <div className="text-[13px] font-medium" style={{ color: 'var(--admin-text)' }}>
              {asesoria.asesor?.asesorId ? (
                <button
                  type="button"
                  className="text-(--admin-blue) hover:underline"
                  onClick={() => navigate(`/asesores/${asesoria.asesor!.asesorId}`)}
                >
                  {asesoria.asesor.nombre}
                </button>
              ) : (
                asesoria.asesor?.nombre || '—'
              )}
            </div>
          </div>

          <div className="admin-detail-field">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--admin-purple)' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--admin-text3)' }}>
                Fecha inicio
              </span>
            </div>
            <div className="text-[15px] font-semibold" style={{ color: 'var(--admin-text)' }}>
              {formatDate(asesoria.fechaInicio)}
            </div>
          </div>

          <div className="admin-detail-field">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-3.5 h-3.5" style={{ color: 'var(--admin-amber)' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--admin-text3)' }}>
                Horas invertidas
              </span>
            </div>
            <div className="text-[15px] font-semibold flex items-center flex-wrap gap-1" style={{ color: 'var(--admin-text)' }}>
              {(asesoria as any)?.horasAntes != null && (asesoria as any)?.horasDespues != null ? (
                <>
                  <OficialPrefix oficial={(asesoria as any).horasOficial} proyectado={(asesoria as any).horasAntes} format={formatHours} />
                  <Tooltip label="Horas acumuladas antes de esta asesoría (proyectadas)" align="left">
                    <span className="text-(--admin-text3) text-[13px]">
                      {formatHours((asesoria as any).horasAntes)} ({((asesoria as any).porcentajeHorasAntes ?? 0).toFixed(1)}%)
                    </span>
                  </Tooltip>
                  <span className="mx-1 text-(--admin-text3)"> → </span>
                  <Tooltip label="Horas acumuladas después de esta asesoría (proyectadas)" align="center">
                    <span className="text-(--admin-green)">
                      {formatHours((asesoria as any).horasDespues)} ({((asesoria as any).porcentajeHorasDespues ?? 0).toFixed(1)}%)
                    </span>
                  </Tooltip>
                  {asesoria.horas != null && (
                    <Tooltip label="Duración de esta asesoría" align="right">
                      <span className="text-gray-500 font-normal text-[13px]"> ({formatHours(asesoria.horas)})</span>
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  {formatHours(asesoria.horas)} {asesoria.horas != null && `(+${((asesoria.horas / 320) * 100).toFixed(1)}%)`}
                </>
              )}
            </div>
          </div>

          <div className="admin-detail-field">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--admin-green)' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--admin-text3)' }}>
                Fecha fin
              </span>
            </div>
            <div className="text-[15px] font-semibold" style={{ color: 'var(--admin-text)' }}>
              {formatDate(asesoria.fechaFin)}
            </div>
          </div>
        </div>

        {recomendacionesLimpias && (
          <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--admin-amber-bg)', border: '1px solid #f0dba0' }}>
            <div
              className="text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2 font-medium"
              style={{ color: 'var(--admin-amber)' }}
            >
              <AlertCircle className="w-3 h-3" />
              Recomendaciones
            </div>
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--admin-text)' }}>
              {recomendacionesLimpias}
            </p>
          </div>
        )}

        {rechazos.length > 0 && (
          <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--admin-red-bg)', border: '1px solid #fecaca' }}>
            <div
              className="text-[10px] uppercase tracking-wider mb-3 flex items-center gap-2 font-bold"
              style={{ color: 'var(--admin-red)' }}
            >
              <AlertCircle className="w-3 h-3" />
              Historial de Rechazos ({rechazos.length})
            </div>
            <div className="space-y-3">
              {rechazos.map((rechazo, idx) => (
                <div key={idx} className="bg-white/60 p-3 rounded-md border border-red-100">
                  <div className="text-[10px] text-red-600 font-mono mb-1">{rechazo.fecha}</div>
                  <div className="text-[12px] text-red-900 font-medium">
                    {rechazo.motivo || <span className="italic opacity-60">Sin motivo especificado</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs Container — Identical layout and styling to AdminRevisionDetailPage */}
      <div className="border border-(--admin-border) rounded-xl overflow-hidden bg-white shadow-xs mt-6">
        <div className="flex bg-(--admin-surface2) border-b border-(--admin-border) px-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 text-[13px] font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === tab.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-[10px] font-semibold">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Active Tab Workspace */}
        <div className="p-4">
          {activeTab === 'evaluacion' && (
            <div>
              {evaluacion?.cambioCombinacion && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                  <b>La empresa cambió de norma después de esta asesoría.</b> Se evaluó con{' '}
                  <b>{evaluacion.cambioCombinacion.usada.nombre}</b>
                  {evaluacion.cambioCombinacion.actual && (
                    <>
                      {' '}
                      y hoy tiene <b>{evaluacion.cambioCombinacion.actual.nombre}</b>
                    </>
                  )}
                  . La evaluación se muestra con las normas con que se hizo.
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className="text-[13px] font-bold text-gray-900">Evaluación NTC</span>
                <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setRevisionView('matrix')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                      revisionView === 'matrix' ? 'bg-white text-gray-900 shadow-xs font-bold' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    Matriz
                  </button>
                  <button
                    type="button"
                    onClick={() => setRevisionView('stepper')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                      revisionView === 'stepper' ? 'bg-white text-gray-900 shadow-xs font-bold' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                    Paso a paso
                  </button>
                </div>
              </div>

              {revisionView === 'matrix' ? (
                evaluacion ? (
                  <div className="p-2">
                    <MatrizInline data={evaluacion} isRevisionMode={false} historialRevision={historialRevision} />
                  </div>
                ) : (
                  <p className="text-[12px] text-gray-400 italic text-center py-8">Cargando datos de evaluación...</p>
                )
              ) : (
                evaluacion &&
                numericAsesoriaId && (
                  <TernaRevisionStepper
                    asesoriaId={numericAsesoriaId}
                    data={evaluacion}
                    diffTernaMap={diffTernaMap}
                    latestHistorialMap={latestHistorialMap}
                    historialRevision={historialRevision}
                    onStepSaved={() => {}}
                  />
                )
              )}
            </div>
          )}

          {activeTab === 'evidencias' &&
            (asesoriaAdaptada ? (
              <EvidenciasTab asesoria={asesoriaAdaptada} refetch={() => {}} canReview={false} />
            ) : (
              <div className="py-12 text-center text-gray-500 text-[13px]">Sin evidencias cargadas</div>
            ))}

          {activeTab === 'audios' &&
            (asesoriaAdaptada ? (
              <AudiosTab asesoria={asesoriaAdaptada} refetch={() => {}} canReview={false} />
            ) : (
              <div className="py-12 text-center text-gray-500 text-[13px]">Sin audios grabados</div>
            ))}

          {activeTab === 'trazabilidad' && numericAsesoriaId && <TrazabilidadTab asesoriaId={numericAsesoriaId} />}

          {activeTab === 'historial' && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-bold text-gray-900 mb-3">Asesorías de otros asesores</h3>
              {!asesoria?.otrasAsesorias || asesoria.otrasAsesorias.length === 0 ? (
                <p className="text-[12px] text-gray-400 italic py-6 text-center">No hay otras asesorías registradas para esta empresa.</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Asesor</th>
                      <th>Fecha inicio</th>
                      <th>Fecha fin</th>
                      <th>Estado</th>
                      <th>Objetivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesoria.otrasAsesorias.map((otra) => (
                      <tr key={otra.asesoriaId}>
                        <td>{otra.asesorNombre}</td>
                        <td>{otra.fechaInicio ? new Date(otra.fechaInicio).toLocaleDateString('es-CO') : '—'}</td>
                        <td>{otra.fechaFin ? new Date(otra.fechaFin).toLocaleDateString('es-CO') : '—'}</td>
                        <td>
                          <span className={`admin-chip admin-chip--${otra.estado?.toLowerCase()}`}>{otra.estado}</span>
                        </td>
                        <td>{otra.objetivo ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {modalType === 'aprobar' && (
        <AprobarModal
          empresaNombre={asesoria.empresa?.nombre || 'Empresa'}
          onClose={closeModal}
          onConfirm={handleAprobar}
          isLoading={aprobando}
          result={aprobarResult}
          error={errorAprobar}
        />
      )}
      {modalType === 'denegar' && (
        <DenegarModal
          empresaNombre={asesoria.empresa?.nombre || 'Empresa'}
          onClose={closeModal}
          onConfirm={handleDenegar}
          isLoading={rechazando}
        />
      )}
    </div>
  );
}
